import { Type } from '@angular/core';

import * as am4charts from '@amcharts/amcharts4/charts';
import { SerialChart } from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import { IExportOptions } from '@amcharts/amcharts4/core';

import { chartColors } from '../../../../../Common/Charts/enums/chart-colors.enum';
import { manualChartExport } from '../../../helpers/manualChartExport.helper';
import { ChartMetadataModel } from '../../chart-metadata.model';
import { ChartModel } from '../../chart.model';
import { CustomLegendItem } from '../../custom-legend-item.model';
import { DataPointModel } from '../../data-point.model';
import { MultiSeriesPieChartOptionsModel } from './multi-series-pie-chart-options.model';
import { ReportSeriesModel } from '../../../../../Common/Reports/models/report-series.model';


export class MultiSeriesPieChartModel extends ChartModel {
    declare amchart: am4charts.PieChart;
    declare series: ReportSeriesModel[];
    chartType: Type<SerialChart> = am4charts.PieChart;
    constructor(
        series: ReportSeriesModel[],
        options: MultiSeriesPieChartOptionsModel = {},
        metadata?: ChartMetadataModel,
    ) {
        super(series, options, metadata);
    }

    public clone(): MultiSeriesPieChartModel {
        const cloneSeries = this.series.map(s => s.clone());

        return new MultiSeriesPieChartModel(cloneSeries, this.options, this.metadata);
    }

    public initChart(element: HTMLElement |am4core.Container, chartType: Type<SerialChart>): void {
        super.initChart(element, chartType);

        this.amchart.svgContainer.htmlElement.style.height = this.options.styles.chartHeight + 'px';
        this.amchart.height = am4core.percent(100);
        this.amchart.radius = am4core.percent(90);
        this.amchart.paddingBottom = 15;
        this.amchart.innerRadius = am4core.percent(this.options.styles.innerRadiusPercent);

        this.titleLabel.fill = am4core.color(chartColors.agprText);
        this.titleLabel.fontWeight = this.options?.styles?.fontWeight ?? '600';

        if (this.options.styles.titleStyles) {
            this.titleLabel.element.addStyle(this.options.styles.titleStyles);
        }
        // TODO: responsiveness

        // sub chart adjustment
        if (element instanceof am4core.Container) {
            this.amchart.paddingTop = 5;
            this.titleLabel.dy = 15;
        }

        // Add and configure Series
        for (let i = 0; i < this.series.length; i++) {
            this.addSeries(i);
        }

        if (this.options.legendContainerElement) {
            this.initCustomLegend();
        }
    }

    public addSeries(index: number, isNoDataSeries: boolean = false) {
        const pieSeries = this.amchart.series.push(new am4charts.PieSeries());
        pieSeries.innerRadius = am4core.percent(this.options.styles.seriesRadiusPercent[index]);
        pieSeries.dataFields.value = 'xValue' + index;
        pieSeries.dataFields.category = 'label';

        // set tooltip style
        pieSeries.tooltip.getFillFromObject = false;
        pieSeries.tooltip.background.fill = am4core.color(chartColors.agprWhite);
        pieSeries.tooltip.background.stroke = am4core.color('#ced4da');
        pieSeries.tooltip.background.cornerRadius = 8;
        pieSeries.tooltip.background.fillOpacity = 1;
        // clear tooltip background shadow
        pieSeries.tooltip.background.filters.clear();
        pieSeries.tooltip.label.padding(0, 0, 0, 0);
        pieSeries.tooltip.label.fill = am4core.color(chartColors.agprBlack);
        pieSeries.tooltip.label.interactionsEnabled = true;
        pieSeries.tooltip.keepTargetHover = true;
        pieSeries.tooltip.label.maxWidth = 300;
        pieSeries.tooltip.label.wrap = true;

        pieSeries.labels.template.disabled = true;
        pieSeries.ticks.template.disabled = true;

        const sliceTemplate = pieSeries.slices.template;
        sliceTemplate.stroke = am4core.color(chartColors.agprWhite);
        sliceTemplate.strokeWidth = 1;
        sliceTemplate.strokeOpacity = 1;
        sliceTemplate.states.getKey('hover').properties.shiftRadius = 0;
        sliceTemplate.states.getKey('hover').properties.scale = 1.05;
        sliceTemplate.states.getKey('hover').transitionDuration = 200;
        sliceTemplate.states.getKey('active').properties.shiftRadius = 0;
        sliceTemplate.adapter.add('tooltipHTML', (text, target) => {
            if (target.dataItem.dataContext['label'] !== 'noData') {
                return (this.options as MultiSeriesPieChartOptionsModel).tooltipHTML
                    .replace('{labelInTooltip}', target.dataItem.dataContext['labelInTooltip' + index])
                    .replace('{viewMentionUrl}',  target.dataItem.dataContext['viewMentionUrl' + index])
                    .replace('{color}', target.dataItem.dataContext['color']);
            }
            return '<div style="margin: 15px 10px; font-size: 0.875rem; font-weight: 600">' + target.dataItem.dataContext['tooltipText'] + '</div>';
        });

        sliceTemplate.adapter.add('fill', (fill, target) => {
            if (target.dataItem.dataContext['label'] !== 'noData') {
                return target.dataItem.dataContext['color'];
            } else {
                const pattern = new am4core.LinePattern();
                pattern.width = 8;
                pattern.height = 8;
                pattern.strokeWidth = 1;
                pattern.stroke = am4core.color(chartColors.agprLighterGrey);
                pattern.backgroundFill = am4core.color(chartColors.agprGreyMediumE5);
                pattern.backgroundOpacity = 1;
                pattern.rotation = 45;
                return pattern;
            }
        });
    }

    getCustomLegendItems(): CustomLegendItem[] {
        return this.options.customLegendItems as CustomLegendItem[];
    }

    public updateData(data: DataPointModel[][]): void {
        this.rawData = data;
        if (!data) {
            this.amchart.data = null;

            return;
        }

        for (const pieSeries of this.amchart.series) {
            pieSeries.tooltip.hideTooltip();
        }
        const flatData: DataPointModel[] = [];
        for (let i = 0; i < data.length; i++) {
            const seriesData = data[i];
            const noDataSlice = flatData.find(slice => slice.label === 'noData');
            const noDataSliceNew = {};
            if (!seriesData) {
                continue;
            }
            // since a pie chart doesn't display is value is zero.. if ALL data points are zero.. show noDataSlice
            const count = seriesData.reduce((previousValue, dataPoint) => previousValue + dataPoint.xValue, 0) || 0;
            if (count === 0 || seriesData.length === 0) {
                if (noDataSlice) {
                    noDataSlice['xValue' + i] = 1;
                } else {
                    noDataSliceNew['label'] = 'noData';
                    noDataSliceNew['xValue' + i] = 1;
                    noDataSliceNew['tooltipText'] = this.metadata.noDataFromDateRangeLabel;
                    noDataSliceNew['labelColor'] = chartColors.agprText;
                    flatData.push(noDataSliceNew);
                }
                continue;
            }
            for (let j = 0; j < seriesData.length; j++) {
                let category = flatData.find(c => c.label === seriesData[j].label);
                if (!category) {
                    const dataPoint = {};
                    dataPoint['label'] = seriesData[j].label;
                    // if seriesData has color, use it, else fall back to the options map, else fall back to grey
                    dataPoint['color'] = seriesData[j].color || this.options.styles?.colorMap[seriesData[j].id] || chartColors.agprGrey;
                    dataPoint['labelColor'] = this.options.styles?.colorMap[seriesData[j].id + '_LABEL'] || chartColors.agprWhite;
                    flatData.push(dataPoint);
                }
                category = flatData.find(c => c.label === seriesData[j].label);
                category['labelInTooltip' + i] = i !== data.length - 1 ? ` (${this.series[i].label})` : '';
                category['xValue' + i] = seriesData[j].xValue;
                category['viewMentionUrl' + i] = seriesData[j].viewMentionUrl;
                if (noDataSlice) {
                    noDataSlice['xValue' + i] = 0;
                } else if (noDataSliceNew) {
                    noDataSliceNew['xValue' + i] = 0;
                }
                // for exporting chart
                const chartTitle = this.metadata?.titleText ? this.metadata.titleText.trim().toUpperCase() : null;
                if (chartTitle) {
                    category[`xValue_${chartTitle}` + i] = seriesData[j].xValue;
                }
            }
        }

        this.amchart.data = JSON.parse(JSON.stringify(flatData));

        this.updateSeriesStyles();
        this.updateCustomLegend();
        this.ready.next(true);
    }


    public updateSeriesStyles(): void {

    }

    public export(type: keyof IExportOptions, filename?: string): Promise<boolean> {
        this.amchart.exporting.filePrefix = filename;
        this.amchart.exporting.useWebFonts = false;

        if (type === 'csv' || type === 'json') {
            const dataFields: object = {};
            dataFields['label'] = (this.options as MultiSeriesPieChartOptionsModel).mentionsLabel;
            dataFields['xValue0'] = 'Previous Period';
            dataFields['xValue1'] = 'Current Period';
            manualChartExport.exportFlatSeriesData(type, this.amchart.data, dataFields, filename);
            return;
        }
        return this.amchart.exporting.export(type);
    }

    public isContentEmpty(): boolean {
        return false;
    }
}
